/**
 * Utilidades para el manejo de fechas en la aplicación
 */

/**
 * Obtiene la fecha y hora actual en formato ISO
 * @returns {string} Fecha y hora en formato ISO
 */
export const getCurrentDateTimeISO = () => {
  return new Date().toISOString();
};

/**
 * Convierte a Date. Acepta fecha ISO, timestamp, Date, o texto
 * "DD/MM/YYYY"/"DD/MM/YY" (años de 2 dígitos: 00-30 -> 2000-2030,
 * 31-99 -> 1931-1999).
 *
 * El texto con "/" nunca se deja parsear directo por `new Date()`/dayjs:
 * lo interpretan como MM/DD (convención EE.UU.) e invierten día y mes en
 * silencio para cualquier fecha ambigua (día <= 12) -- por eso se reordena
 * a mano a YYYY-MM-DD antes de construir la fecha.
 *
 * @param {string|number|Date} value - Fecha a convertir.
 * @returns {Date|null} - Date válido o null si no se pudo parsear.
 */
export const parseDate = (value) => {
  if (!value) return null;

  let normalized = value;
  if (typeof value === "string" && value.includes("/")) {
    const parts = value.split("/");
    if (parts.length !== 3) return null;
    const day = parts[0].padStart(2, "0");
    const month = parts[1].padStart(2, "0");
    let year = parts[2];
    if (year.length === 2) {
      const yearNum = parseInt(year, 10);
      year = yearNum <= 30 ? `20${year}` : `19${year}`;
    }
    normalized = `${year}-${month}-${day}`;
  }

  const date = new Date(normalized);

  // Validate date
  if (isNaN(date.getTime())) return null;

  // Min date 01/01/0001
  if (date.getFullYear() <= 1) return null;

  return date;
};

/**
 * Formatea una fecha a formato legible (DD/MM/YYYY). Ver `parseDate` para
 * los formatos de entrada aceptados.
 *
 * @param {string|number|Date} value - Fecha a formatear.
 * @returns {string} - Fecha formateada o '-' si no es válida.
 */
export const formatDate = (value) => {
  const date = parseDate(value);
  if (!date) return "-";

  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  });
};

/**
 * Formatea una fecha en el formato AAAA-MM-DD que esperan las acciones
 * `Importaciones_*` de Business Central.
 *
 * Los componentes se leen en UTC, no en hora local, por dos bugs reales que
 * tenía la versión anterior de este formateo (ambos reproducidos con datos de
 * `ListaTPV`):
 *
 * 1. Las fechas que llegan de BC se guardan como `new Date("2025-08-01")`, que
 *    JS interpreta como medianoche UTC. Leerla con getters locales en un huso
 *    negativo (México, UTC-6) devuelve el día anterior, así que toda fecha se
 *    reenviaba corrida un día: "2025-08-01" salía como "2025-07-31".
 * 2. BC usa 0001-01-01 como "sin fecha". En huso negativo eso cae al año 0, y
 *    se enviaba "0-12-31", que BC rechaza con "no tiene un formato valido".
 *    `parseDate` ya descarta año <= 1, así que ese caso se manda vacío, que es
 *    justamente lo que representa.
 *
 * El año se rellena a 4 dígitos porque el formato de BC lo exige.
 *
 * @param {string|number|Date} value - Fecha a formatear.
 * @returns {string} - Fecha como AAAA-MM-DD, o "" si no hay fecha válida.
 */
export const formatDateForBC = (value) => {
  const parsed = parseDate(value);
  if (!parsed) return "";

  return (
    String(parsed.getUTCFullYear()).padStart(4, "0") +
    "-" +
    String(parsed.getUTCMonth() + 1).padStart(2, "0") +
    "-" +
    String(parsed.getUTCDate()).padStart(2, "0")
  );
};

/**
 * Convierte una fecha de formato DD/MM/YYYY a formato ISO
 * @param {string} dateString - Fecha en formato DD/MM/YYYY
 * @returns {string} - Fecha en formato ISO o null si es inválida
 */
export const dateStringToISO = (dateString) => {
  if (!dateString) return null;

  try {
    const parts = dateString.split("/");
    if (parts.length !== 3) return null;

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Los meses en JS son 0-11
    const year = parseInt(parts[2], 10);

    const date = new Date(year, month, day);
    return date.toISOString();
  } catch (error) {
    console.error("Error al convertir fecha:", error);
    return null;
  }
};
